import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { extractPaywallDetail } from "@/lib/subscribe";
import { useAuth } from "@/context/AuthContext";
import { MapPin, Clock, Euro, Briefcase, CalendarDays, ArrowLeft, User, Rocket, Flame } from "lucide-react";
import { toast } from "sonner";
import ContactModal from "@/components/ContactModal";
import PaywallModal from "@/components/PaywallModal";

export default function JobDetail() {
  const { id } = useParams();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [boosting, setBoosting] = useState(false);
  const [contact, setContact] = useState(null);
  const [paywall, setPaywall] = useState(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => setJob(false)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="max-w-4xl mx-auto p-10 text-neutral-500 uppercase tracking-widest animate-pulse">Loading…</div>;
  if (!job) return <div className="max-w-4xl mx-auto p-10">Job not found. <Link to="/jobs" className="text-amber-500">Back</Link></div>;

  const isOwner = user && user.id === job.employer_id;
  const canApply = user && !isOwner;

  const apply = async () => {
    if (!user) { navigate("/login"); return; }
    setApplying(true);
    try {
      const { data } = await api.post(`/apply/job/${job.id}`, { message });
      setContact(data);
      toast.success("Application sent — contact revealed");
    } catch (e) {
      const detail = extractPaywallDetail(e);
      if (detail) { setPaywall(detail); setApplying(false); return; }
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to apply");
    } finally {
      setApplying(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this job listing?")) return;
    try {
      await api.delete(`/jobs/${job.id}`);
      toast.success("Job removed");
      navigate("/dashboard");
    } catch { toast.error("Failed to delete"); }
  };

  const boost = async () => {
    setBoosting(true);
    // First-boost-free gift: if user hasn't used their free boost yet, redeem it directly
    if (user && !user.free_boost_used) {
      try {
        const { data } = await api.post("/boost/free", { target_type: "job", target_id: job.id });
        setJob({ ...job, boosted_until: data.boosted_until, is_boosted: true });
        toast.success("Boost activated — free on us! Your job is URGENT for 7 days.");
        try { await refresh(); } catch (e) { console.warn("[JobDetail] refresh() failed after free boost", e); }
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to activate free boost");
      } finally {
        setBoosting(false);
      }
      return;
    }
    // Otherwise: paid Stripe checkout
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "job_boost_7d",
        origin_url: window.location.origin,
        metadata: { job_id: job.id },
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to start boost");
      setBoosting(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
      <Link to="/jobs" data-testid="back-to-jobs" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-400 hover:text-amber-500 mb-6">
        <ArrowLeft className="w-3 h-3" /> Back to jobs
      </Link>
      <div className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-amber-500 text-xs font-bold tracking-[0.3em] uppercase">{job.trade}</div>
            {job.is_boosted && (
              <span data-testid="urgent-badge" className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500 text-neutral-950 text-[10px] font-bold uppercase tracking-widest">
                <Flame className="w-3 h-3" /> Urgent · Boosted
              </span>
            )}
          </div>
          <h1 data-testid="job-title" className="display text-4xl md:text-5xl mt-2">{job.title}</h1>
          <div className="mt-4 flex flex-wrap gap-4 text-xs uppercase tracking-wide text-neutral-300">
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-amber-500" />{job.county}{job.town ? `, ${job.town}` : ""}</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-amber-500" />{job.duration}</span>
            {job.start_date && <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3 text-amber-500" />Starts {job.start_date}</span>}
            <span className="flex items-center gap-1"><User className="w-3 h-3 text-amber-500" />Posted by {job.employer_name}</span>
          </div>
          {job.counties && job.counties.length > 0 && (
            <div data-testid="job-extra-counties" className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest text-neutral-500 mr-1">Also covers</span>
              {job.counties.map((c) => (
                <span key={c} className="text-[11px] uppercase tracking-wide bg-neutral-900 border border-amber-500/40 text-amber-500 px-2 py-1">{c}</span>
              ))}
            </div>
          )}
          <div className="mt-8 border-t border-neutral-800 pt-6">
            <div className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-2">Job Description</div>
            <p data-testid="job-description" className="text-neutral-200 leading-relaxed whitespace-pre-line">{job.description}</p>
          </div>
        </div>

        <aside className="md:col-span-1">
          <div className="border border-neutral-800 bg-neutral-900 p-6 sticky top-20">
            <div className="text-xs font-bold uppercase tracking-widest text-neutral-500">Day Rate</div>
            <div className="display text-4xl text-amber-500 mt-1 flex items-center">
              {job.day_rate ? (<><Euro className="w-6 h-6" />{job.day_rate}</>) : "DOE"}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Per Day</div>

            <div className="mt-6 border-t border-neutral-800 pt-6 space-y-3">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-400">
                <Briefcase className="w-3 h-3 text-amber-500" />{job.duration} contract
              </div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-400">
                <MapPin className="w-3 h-3 text-amber-500" />{job.county}
              </div>
            </div>

            {isOwner ? (
              <>
                {!job.is_boosted && (
                  <button
                    data-testid="boost-job-btn"
                    onClick={boost}
                    disabled={boosting}
                    className="mt-6 w-full py-3 bg-amber-500 text-neutral-950 uppercase tracking-widest text-xs font-bold hover:bg-amber-400 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                  >
                    <Rocket className="w-4 h-4" />
                    {boosting ? "Redirecting…" : (user && !user.free_boost_used ? "Boost — FREE (first gift)" : "Boost — €9.99 · 7 days")}
                  </button>
                )}
                {!job.is_boosted && user && !user.free_boost_used && (
                  <div className="mt-1 text-[10px] uppercase tracking-widest text-amber-500 text-center">Your first boost is on us · €0</div>
                )}
                {!job.is_boosted && user && user.free_boost_used && (
                  <div className="mt-1 text-[10px] uppercase tracking-widest text-neutral-500 text-center">+ VAT where applicable</div>
                )}
                {!job.is_boosted && user && user.free_boost_used && (
                  <div data-testid="boost-promo-banner" className="mt-3 border border-amber-500/60 bg-amber-500/5 p-3 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-amber-500 font-bold">Launch offer · 50% off first boost</div>
                    <div className="mt-1 text-xs text-neutral-300">
                      Use code <span className="font-mono font-bold text-amber-400 tracking-wider">LAUNCH50</span> at checkout
                    </div>
                    <div className="mt-1 text-[10px] text-neutral-500">First-time customers only · limited redemptions</div>
                  </div>
                )}
                {job.is_boosted && (
                  <div data-testid="boost-active" className="mt-6 border border-amber-500 bg-amber-500/10 p-3 text-center">
                    <div className="text-xs uppercase tracking-widest text-amber-500 font-bold">Boost active</div>
                    <div className="text-[10px] text-neutral-400 mt-1">Until {new Date(job.boosted_until).toLocaleDateString()}</div>
                  </div>
                )}
                <button data-testid="delete-job-btn" onClick={remove} className="mt-3 w-full py-3 border border-red-800 text-red-300 hover:bg-red-950/50 uppercase tracking-widest text-xs font-bold transition-colors">
                  Delete Job
                </button>
              </>
            ) : (
              <>
                {canApply ? (
                  <>
                    <textarea
                      data-testid="job-apply-message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Short note to the employer (optional)"
                      rows={3}
                      className="mt-6 w-full bg-neutral-950 border border-neutral-800 focus:border-amber-500 outline-none p-3 text-sm text-neutral-200"
                    />
                    <button
                      data-testid="job-apply-btn"
                      onClick={apply}
                      disabled={applying}
                      className="mt-3 w-full py-3 bg-amber-500 text-neutral-950 uppercase tracking-widest text-xs font-bold hover:bg-amber-400 transition-colors disabled:opacity-60"
                    >
                      {applying ? "Sending…" : "Apply · Reveal Contact"}
                    </button>
                  </>
                ) : (
                  <Link to="/login" data-testid="job-apply-login-btn" className="mt-6 block text-center w-full py-3 bg-amber-500 text-neutral-950 uppercase tracking-widest text-xs font-bold hover:bg-amber-400 transition-colors">
                    Log in to Apply
                  </Link>
                )}
              </>
            )}
          </div>
        </aside>
      </div>

      {contact && (
        <ContactModal
          contact={contact}
          onClose={() => setContact(null)}
          title="Contact revealed"
          subtitle={`For: ${job.title}`}
        />
      )}
      {paywall && <PaywallModal detail={paywall} onClose={() => setPaywall(null)} />}
    </div>
  );
}
